import React from "react";
import Button from "web/react/components/__LEGACY__/LEGACY_button/button";
// eslint-disable-next-line css-modules/no-unused-class
import Conditional from "web/react/components/conditional/conditional";
import LeadLink from "web/react/components/lead-link/lead-link";
import OOSPill from "web/react/components/oos-pill/oos-pill";
import ProductGallery from "web/react/components/product-gallery/product-gallery";
import { RelatedProductsShowMore } from "web/react/components/related-products-show-more/related-products-show-more";
import SaveForLaterButton from "web/react/components/save-for-later-button/save-for-later-button";
import { Text } from "web/react/emo/text/text";
import { VStack } from "web/react/emo/v-stack";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import userProfiler from "web/script/modules/userprofiler";

import {
    OOSProductHeroSerializer,
    RelatedProductsAreaSectionSerializer,
    RetailerDataSerializer,
} from "web/types/serializers";
import styles from "./oos-product-hero.module.css";

function getShowMoreCTAText(
    category: any,
    relatedUrl: string
): { showMoreCTAText: string; showMoreLink: string } {
    const linkType = environment.getFeature("eac_oos_pdp_redesign");
    if (linkType === "variant_category" && category?.name) {
        // "See more {category_name}"
        return {
            showMoreCTAText: gettext("oos_product.see_more_category.cta", {
                category_name: category.name,
            }),
            showMoreLink: category.url,
        };
    }
    // "See similar products"
    return { showMoreCTAText: gettext("oos_product.hero.btn"), showMoreLink: relatedUrl };
}

export interface OOSProductHeroProps {
    data: OOSProductHeroSerializer;
    variant: "icon" | "affiliate";
    productUrl?: string;
    sections?: RelatedProductsAreaSectionSerializer[];
    linkId?: string;
    retailerData: RetailerDataSerializer | null;
}

function OOSProductHero({
    data,
    variant,
    sections,
    linkId,
    retailerData,
}: OOSProductHeroProps): React.ReactElement {
    const {
        product_header: productHeader,
        oos_product_header: oosProductHeader,
        product_image_gallery: productImageGallery,
    } = data;
    const section = sections?.find((section) => {
        return section.section_type === "similar";
    });

    const newDesignVariant = environment.getFeature("eac_oos_pdp_redesign");
    const { showMoreCTAText, showMoreLink } = getShowMoreCTAText(
        oosProductHeader.product_category,
        section?.view_more_url || ""
    );

    const { lead_push_link: leadPushLink } = data;
    const isAffiliate = variant === "affiliate";

    const goToRetailerButtonText = gettext(
        "oos_product.last_seen_for_price_at_retailer_link_btn_text",
        {
            retailer_name: retailerData?.name,
        }
    );

    return (
        <article className={styles.hero}>
            <div className={styles.sectionHolder}>
                {productImageGallery?.images && (
                    <div className={styles.leftSection}>
                        <ProductGallery
                            images={productImageGallery.images}
                            currentLinkId={linkId}
                            hasRelatedProductsSidebar={false}
                            productId={data.product_header.product_id}
                        />
                    </div>
                )}
                <VStack spacing={"xxs"}>
                    <div className={styles.info}>
                        <div className={styles.headerSection}>
                            <h1 className={styles.title}>
                                {productHeader.is_owner_active &&
                                productHeader.is_owner_feed_linkable ? (
                                    <a
                                        className={styles.designer}
                                        href={productHeader.designer_url}
                                    >
                                        {productHeader.owner_name + " "}
                                    </a>
                                ) : (
                                    productHeader.owner_name
                                )}
                                {!!productHeader.short_description && (
                                    <Text as={"p"} textStyle={"body-2"} color={"secondary"}>
                                        {productHeader.short_description}
                                    </Text>
                                )}
                            </h1>
                            <div className={styles.s4LWrapper}>
                                <SaveForLaterButton
                                    type="icon"
                                    pageSource="oos"
                                    isLoggedIn={userProfiler.isLoggedIn()}
                                    productId={productHeader.product_id}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={styles.ctaArea}>
                        <Conditional check={isAffiliate} key="affiliate-lead">
                            <>
                                <OOSPill text={gettext("oos_product.oos.label")} />
                                {oosProductHeader.last_seen_for_price_at_retailer_signup_bis_alert_desc && (
                                    <p
                                        className="mt0"
                                        dangerouslySetInnerHTML={{
                                            __html: oosProductHeader.last_seen_for_price_at_retailer_signup_bis_alert_desc, // Last seen for <em><span {currency_symbol_span_props}>{currency_symbol}</span><span {price_span_props}>{price}</span></em> at <a {retailer_link_props}>{retailer_name}</a>.
                                        }}
                                    />
                                )}
                                {leadPushLink && (
                                    <>
                                        <div className={styles.oosCTA}>
                                            <SaveForLaterButton
                                                className={styles.s4lButton}
                                                cta={gettext("oos_product.signup_bis_alert.cta")}
                                                type="LEGACY_tick"
                                                pageSource="oos"
                                                isLoggedIn={userProfiler.isLoggedIn()}
                                                productId={productHeader.product_id}
                                                appDeeplinkPath={showMoreLink}
                                            />
                                        </div>
                                        <div className={styles.oosCTA}>
                                            {retailerData?.is_high_value_retailer ? (
                                                <LeadLink
                                                    href={leadPushLink}
                                                    rel="nofollow"
                                                    openInNewTab={true}
                                                    reason="affiliate-buy-on-store-button"
                                                    beforeOnClick={(process, event) => {
                                                        analytics.event(
                                                            "oos-product-hero-button",
                                                            "click",
                                                            "affiliate-buy-on-store-button"
                                                        );
                                                        process(event);
                                                    }}
                                                >
                                                    <Button
                                                        styleType={"primary"}
                                                        disabled={false}
                                                        fullWidth={true}
                                                        fullHeight={false}
                                                        openInNewTab={false}
                                                        className={styles.goToRetailerButton}
                                                        aria-label={goToRetailerButtonText}
                                                    >
                                                        {goToRetailerButtonText}
                                                    </Button>
                                                </LeadLink>
                                            ) : (
                                                <RelatedProductsShowMore
                                                    view_more_url={showMoreLink}
                                                    product_is_instock={
                                                        section?.product_is_instock as boolean
                                                    }
                                                    isAffiliate={isAffiliate}
                                                    buttonType={"relatedProductsOOS"}
                                                    showMoreCTAText={showMoreCTAText}
                                                    analyticsEventAction={
                                                        newDesignVariant === "variant_category"
                                                            ? "show_more_category"
                                                            : "show_more_related"
                                                    }
                                                    retailerSlug={retailerData?.slug || null}
                                                />
                                            )}
                                        </div>
                                    </>
                                )}
                            </>
                        </Conditional>
                    </div>
                </VStack>
            </div>
        </article>
    );
}

export default OOSProductHero;
